import React from "react";
import { useNavigate } from "react-router-dom";

/** Pipeline steps for one turn.
 *
 *  The generated SQL is deliberately not rendered — it is an implementation
 *  detail, and showing it invites users to read the query instead of the
 *  answer. It still arrives on the socket and is kept in the turn's state for
 *  debugging.
 */
export function StepCard({ steps, route, open }) {
  const last = steps[steps.length - 1];
  return (
    <div className="ap-msg">
      <div className="ap-tool">
        <div className="ap-tool-head">
          <span className={`ap-dot ${open ? "ap-live" : "ap-ok"}`} />
          <span className="ap-mono">{route || "pipeline"}</span>
          <span className="ap-tool-meta ap-mono">
            {open ? "running…" : last ? `${(last.ms / 1000).toFixed(1)}s` : ""}
          </span>
        </div>
        <div className="ap-tool-body">
          {steps.map((s, i) => (
            <div
              key={`${s.key}-${i}`}
              className={`ap-step ${open && i === steps.length - 1 ? "ap-live" : "ap-done"}`}
            >
              <b>{s.label}</b>
              {s.detail ? <span>{s.detail}</span> : null}
              <span className="ap-t ap-mono">{(s.ms / 1000).toFixed(1)}s</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Identifier-ish columns keep their raw value — 1,234 is wrong for an id.
 *  Also what hideIdColumns drops from the table: database keys mean nothing to
 *  the reader and should not leave the system.
 *
 *  Anchored so only a real suffix matches. "paid", "valid" and "uuid" end in
 *  the same three letters and are ordinary columns. */
const ID_COLUMN = /(^|_)id$/i;

/** Columns worth showing: everything that is not a database id.
 *
 *  If that leaves nothing — "SELECT role_id FROM roles" — the originals are
 *  returned instead. An empty table reading "13 rows x 0 cols" is a worse
 *  outcome than showing the id it was asked for. */
function hideIdColumns(columns) {
  const visible = columns.filter((c) => !ID_COLUMN.test(c));
  return visible.length ? visible : columns;
}


/** Name columns that point at a PINE page, and the id column carrying the
 *  target. The id is selected but hidden, so both are in the row. */
const LINKED = {
  company_name: { id: "company_id", path: "/client/view" },
  client_name: { id: "company_id", path: "/client/view" },
  candidate_name: { id: "candidate_id", path: "/candidates/view" },
  role_name: { id: "role_id", path: "/role/view" },
};

/** A link is built only from an id the database returned. Anything that is not
 *  a positive integer stays plain text — a wrong link is worse than none. */
function linkFor(column, row) {
  const rule = LINKED[column];
  if (!rule) return null;

  const raw = row && row[rule.id];
  if (raw === null || raw === undefined || raw === "") return null;

  const id = Number(raw);
  if (!Number.isInteger(id) || id <= 0) return null;

  return `${rule.path}/${id}`;
}

/** MySQL hands back DECIMAL as a string like "932715099.56861000". */
function formatCell(column, value) {
  if (value === null || value === undefined) return "";
  if (ID_COLUMN.test(column)) return String(value);

  const n = typeof value === "number" ? value : Number(value);
  if (typeof value === "boolean" || value === "" || Number.isNaN(n)) {
    return String(value);
  }

  return n.toLocaleString("en-IN", {
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  });
}

/** Build a tab-separated block. Excel and Google Sheets both split TSV across
 *  cells on paste, so this lands as a real table rather than one long string.
 *  Raw values are used, not the display-formatted ones — grouped digits like
 *  "93,27,15,099" paste as text and stop being numbers in the spreadsheet. */
function toTSV(columns, rows) {
  const TAB = String.fromCharCode(9);
  const NL = String.fromCharCode(10);
  const CR = String.fromCharCode(13);

  const clean = (v) => {
    if (v === null || v === undefined) return "";
    return String(v).split(TAB).join(" ").split(CR).join(" ").split(NL).join(" ");
  };

  return [
    columns.join(TAB),
    ...rows.map((r) => columns.map((c) => clean(r?.[c])).join(TAB)),
  ].join(NL);
}

/** Analytics result rows — every row and column, scrollable, copyable. */
export function ResultTable({ data }) {
  const [copied, setCopied] = React.useState(false);
  const navigate = useNavigate();

  if (!data?.columns?.length || !data?.rows?.length) return null;

  // COPY builds its TSV from these too, on purpose. Hiding an id on screen and
  // then pasting it into a spreadsheet that gets emailed would defeat the point.
  const cols = hideIdColumns(data.columns);
  const rows = data.rows;

  const copy = async () => {
    const tsv = toTSV(cols, rows);
    try {
      await navigator.clipboard.writeText(tsv);
    } catch {
      // Clipboard API needs a secure context and can be blocked outright;
      // fall back to the old selection trick so the button still works.
      const ta = document.createElement("textarea");
      ta.value = tsv;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* nothing else to try */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="ap-msg">
      <div className="ap-tool">
        <div className="ap-tool-head">
          <span className="ap-dot" />
          <span className="ap-mono">result_rows</span>
          <span className="ap-tool-meta ap-mono">
            {rows.length} row{rows.length === 1 ? "" : "s"} x {cols.length} col
            {cols.length === 1 ? "" : "s"}
          </span>
          <button type="button" className="ap-copy" onClick={copy}>
            {copied ? "copied" : "copy"}
          </button>
        </div>
        <div className="ap-tool-body ap-tablewrap">
          <table className="ap-table">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {cols.map((c) => {
                    const href = linkFor(c, r);
                    const text = formatCell(c, r?.[c]);
                    return (
                      <td key={c}>
                        {href ? (
                          <a
                            className="ap-link"
                            href={href}
                            onClick={(e) => {
                              // Ctrl/Cmd/shift-click and middle-click keep the
                              // browser's own behaviour — the href is real.
                              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                              e.preventDefault();
                              navigate(href);
                            }}
                          >
                            {text}
                          </a>
                        ) : (
                          text
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
